import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Palette, AlertTriangle } from 'lucide-react';
import { axios, auth, mbUrl, STATUS_LABEL, NO_PERM, eligibleForTest, muted, primary, runCall, toast } from './mbApi';
import { VersionRow } from './VersionRow';
import { EditBriefDialog } from './EditBriefDialog';
import { HandoffControl } from './HandoffControl';
import { BriefImages, RefWarning, VideoLinks } from './BriefRefs';
import { ArchiveToggle } from './ArchiveToggle';
import { TestChecklist } from './TestChecklist';

const BRIEF_ROWS = [['audience_problem', 'Masalah audiens'], ['angle', 'Angle'], ['hook', 'Hook'], ['offer', 'Offer'], ['cta', 'CTA'], ['hypothesis', 'Hipotesis']];

export const Help = ({ children }) => <p className={`text-sm ${muted} rounded-lg bg-white/[0.03] px-3 py-2`}>{children}</p>;

export const taskData = (w, board, dash, showArchived) => {
  const versions = board.creatives.filter((c) => c.workflow_id === w.id && (showArchived || !c.archived));
  const testsOf = (cid) => board.tests.filter((t) => t.creative_id === cid && (showArchived || !t.archived));
  return {
    versions, testsOf, labelOf: Object.fromEntries(versions.map((v) => [v.id, v.version_label])),
    results: Object.fromEntries((dash?.per_test || []).map((r) => [r.test_id, r])),
    vTotals: Object.fromEntries((dash?.per_version || []).map((r) => [r.creative_id, r])),
    eligible: eligibleForTest(board.creatives.filter((c) => c.workflow_id === w.id)),
    stagePerm: (w.current_stage || 'brief') === 'review' ? 'brief' : (w.current_stage || 'brief'),
  };
};

const vprops = ({ ibId, w, d, caps, board, reload, simulate }, c) => ({
  ibId, c, parentLabel: d.labelOf[c.parent_version_id], tests: d.testsOf(c.id), results: d.results, versionTotals: d.vTotals[c.id],
  canCreative: caps.creative && !w.archived, canTest: caps.test && !w.archived, canLearn: (caps.brief || caps.creative || caps.test) && !w.archived,
  canBrief: caps.brief && !w.archived, suggestionId: w.source_suggestion_id, canManage: caps.manage, reload, simulate,
  stageOk: ['test', 'review'].includes(w.current_stage || 'brief'),
  learnings: (board.learnings || []).filter((l) => l.creative_id === c.id),
});

const BriefDetails = ({ ibId, w }) => (
  <div className={`text-sm ${muted} space-y-1`} data-testid={`mb-brief-details-${w.id}`}>
    {BRIEF_ROWS.map(([k, label]) => <p key={k}><span className={primary}>{label}:</span> {w[k] || <span className="italic opacity-80">belum diisi</span>}</p>)}
    {(w.reference_links || []).map((l) => <a key={l} href={l} target="_blank" rel="noreferrer" className="block underline break-all">{l}</a>)}
    <VideoLinks links={w.video_links} />
    <BriefImages ibId={ibId} ids={w.ref_images} testid={`mb-brief-images-${w.id}`} />
    {w.code && <RefWarning refs={(w.reference_links || []).length > 0} images={(w.ref_images || []).length > 0} videos={(w.video_links || []).length > 0} />}
    {w.brief && <p className="whitespace-pre-wrap">{w.brief}</p>}
    {w.brief_pdf_url && <a href={w.brief_pdf_url} target="_blank" rel="noopener noreferrer" className="underline text-[#F77F00]" data-testid={`mb-workflow-pdf-${w.id}`}>PDF brief ↗</a>}
  </div>
);

const CreativeRequestButton = ({ ibId, w, reload }) => {
  const [busy, setBusy] = useState(false);
  if (w.branch !== 'creative_team') return null;
  if (w.creative_request_id) {
    return (
      <p className="text-sm">
        <Link to="/team/creative-request" className="underline text-[hsl(var(--primary))]" data-testid={`mb-cr-link-${w.id}`}>Creative Request linked ↗</Link>
        <span className={`block text-xs ${muted}`}>Request siap ≠ versi lulus. Lepas terima hasil, Firzan masih kena Add Version + link bahan + review.</span>
      </p>
    );
  }
  const send = () => runCall(setBusy, async () => {
    await axios.post(mbUrl(ibId, `/workflows/${w.id}/creative-request`), { request_type: 'poster' }, auth());
    reload();
  }, 'Request dihantar ke Creative Team');
  return (
    <Button size="sm" variant="outline" className="h-10" disabled={busy} onClick={send} data-testid={`mb-cr-create-${w.id}`}>
      <Palette size={14} className="mr-1.5" /> Hantar ke Creative Team
    </Button>
  );
};

const BoardStatus = ({ ibId, w, canEdit, reload }) => {
  const setStatus = async (status) => {
    try { await axios.put(mbUrl(ibId, `/workflows/${w.id}`), { status }, auth()); reload(); } catch (e) { toast.error('Gagal tukar status papan'); }
  };
  return (
    <div className="space-y-1">
      <Label htmlFor={`mb-status-${w.id}`} className={`text-sm ${primary}`}>Status papan</Label>
      <Select value={w.status} onValueChange={setStatus} disabled={!canEdit || w.archived}>
        <SelectTrigger id={`mb-status-${w.id}`} className="h-10 w-56" data-testid={`mb-status-${w.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
      </Select>
      <p className={`text-xs ${muted}`}>Label paparan sahaja — tahap & PIC ditukar melalui Serah di atas.</p>
    </div>
  );
};

export const TaskSummary = ({ ibId, w, caps, d, reload }) => (
  <div className="space-y-5">
    <BriefDetails ibId={ibId} w={w} />
    {caps.brief && !w.archived && <EditBriefDialog ibId={ibId} w={w} reload={reload} />}
    <HandoffControl ibId={ibId} w={w} canHandoff={caps[d.stagePerm]} eligible={d.eligible} reload={reload} />
    {caps.creative && !w.archived && <CreativeRequestButton ibId={ibId} w={w} reload={reload} />}
    <BoardStatus ibId={ibId} w={w} canEdit={caps.brief || caps.creative || caps.test} reload={reload} />
    {((caps.brief && !w.archived) || (caps.manage && w.archived)) && (
      <div className="rounded-lg border border-red-500/30 p-3 space-y-1" data-testid={`mb-task-risk-${w.id}`}>
        <p className="text-sm font-semibold text-red-400 flex items-center gap-1.5"><AlertTriangle size={15} /> Tindakan berisiko</p>
        <ArchiveToggle ibId={ibId} kind="workflows" rec={w} label={`task ${w.code || w.title}`} reload={reload} testid={`mb-workflow-archive-${w.id}`}
          withText className="border border-[hsl(var(--border))] rounded-md px-3" />
      </div>
    )}
  </div>
);

const AddVersion = ({ ibId, w, reload }) => {
  const [f, setF] = useState({ version_label: '', asset_link: '', format: '', angle: w.angle || '', hook: w.hook || '' });
  const [busy, setBusy] = useState(false);
  const add = () => runCall(setBusy, async () => {
    await axios.post(mbUrl(ibId, '/creatives'), { workflow_id: w.id, ...f }, auth());
    setF({ ...f, version_label: '', asset_link: '' });
    reload();
  }, 'Versi ditambah');
  const fields = [['version_label', 'Label versi', 'V1-Hook-A'], ['asset_link', 'Link bahan', 'https://drive.google.com/…'], ['format', 'Format', 'poster / video'], ['angle', 'Angle', ''], ['hook', 'Hook', '']];
  return (
    <div className="rounded-lg border border-dashed border-[hsl(var(--border))] p-3 space-y-2" data-testid={`mb-version-form-${w.id}`}>
      <p className={`text-sm font-semibold ${primary}`}>Tambah versi bahan</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {fields.map(([k, label, ph]) => (
          <div key={k} className="space-y-1">
            <Label htmlFor={`mb-version-${k}-${w.id}`} className={`text-xs ${muted}`}>{label}</Label>
            <Input id={`mb-version-${k}-${w.id}`} className="h-10" placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`mb-version-${k}-${w.id}`} />
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" className="h-10" disabled={busy || !f.version_label.trim()} onClick={add} data-testid={`mb-version-add-${w.id}`}>
        <Plus size={14} className="mr-1" /> Add Version
      </Button>
    </div>
  );
};

export const TaskMaterials = (p) => {
  const { ibId, w, d, caps, reload } = p;
  return (
    <div className="space-y-3">
      <Help>Firzan: tambah versi (label + link bahan https://), kemudian review Lulus atau Kena ubah. Request Creative Team siap tidak automatik jadi versi lulus.</Help>
      {d.versions.length === 0 && <p className={`text-sm ${muted}`} data-testid={`mb-no-versions-${w.id}`}>Belum ada versi bahan.</p>}
      {d.versions.map((c) => <VersionRow key={c.id} section="bahan" {...vprops(p, c)} />)}
      {caps.creative && !w.archived ? <AddVersion ibId={ibId} w={w} reload={reload} /> : !w.archived && <p className={`text-xs ${muted}`}>Tambah bahan: {NO_PERM.creative}</p>}
    </div>
  );
};

export const TaskTests = (p) => {
  const { w, d, board } = p;
  const ready = d.versions.filter((c) => d.eligible.some((e) => e.id === c.id) || d.testsOf(c.id).length);
  const blocked = d.versions.filter((c) => !ready.includes(c));
  return (
    <div className="space-y-3">
      <TestChecklist w={w} d={d} board={board} />
      {ready.length === 0 && <p className={`text-sm ${muted}`}>Belum ada versi lulus review — ujian belum boleh dicipta.</p>}
      {ready.map((c) => <VersionRow key={c.id} section="ujian" {...vprops(p, c)} />)}
      {blocked.length > 0 && <p className={`text-xs ${muted}`}>Tiada ujian untuk: {blocked.map((c) => c.version_label).join(', ')} (belum lulus review / tiada link bahan).</p>}
    </div>
  );
};

export const TaskLearning = (p) => {
  const { d } = p;
  return (
    <div className="space-y-3">
      <Help>Rekod apa yang dipelajari daripada hasil setiap versi. Cadangan AI ialah draf — Kaer semak sebelum jadi brief.</Help>
      {d.versions.length === 0 && <p className={`text-sm ${muted}`}>Belum ada versi bahan.</p>}
      {d.versions.map((c) => <VersionRow key={c.id} section="belajar" {...vprops(p, c)} />)}
    </div>
  );
};
